/**
 * OWNER-DAILY-EOD-REPORT-V2 (founder ruling 2026-08-19) --
 * /dashboard/reports/money-console, the owner's Daily / EOD Report.
 *
 * The report descends in importance rather than presenting six equal
 * cards. THREE VISUAL WEIGHTS, in order:
 *   HERO       the KPI band -- six current-period business facts.
 *   SUPPORTING treatments (the primary business table), cash (compact),
 *              incentives (one line).
 *   DETAILS    clinics, and the definitions/evidence affordance.
 * Five seconds gives business health; thirty seconds gives why; one click
 * on a number opens the visits behind it.
 *
 * P1 IS A PRESENTATION PACKAGE OVER EXISTING TRUTH. Every figure is read
 * from the truth owners that already exist -- lib/owner-money-console.ts
 * composes walk-ins, the call-centre split, conversion, revenue, ticket
 * size and the treatment rows; lib/accounts/branch-money-day.ts owns the
 * cash. Nothing here re-derives business logic in the frontend.
 *
 * NO COMPARISON IS RENDERED (ruling DECISION 2). The current read path
 * carries no prior-period object, no comparison-period data path and no
 * per-day series, so this report shows current-period truth only: no
 * movement chips, no sparklines, no materiality verdicts, no invented
 * previous-period values -- and no placeholder standing in for any of
 * them. OWNER-CONSOLE-COMPARISON-PERIOD-V1 owns that work.
 *
 * PLAIN LANGUAGE ON THE GLASS: no engineering vocabulary and no design
 * commentary on the default surface. Definitions, derivation detail and
 * the ruled verbatim footnote live behind the closed definitions
 * affordance. Drills default CLOSED: the owner first sees the answer.
 *
 * PLACEMENT: the report lives UNDER Reports via the door #579 shipped;
 * this package changes no navigation. Admission is server-side
 * (requireOwnerScope) -- the nav is never a permission.
 *
 * REUSE: composes the registered ui/ primitives and the scope-
 * parameterised dashboard/reporting components, which take their scope as
 * a parameter and hold no Owner or all-clinics assumption of their own.
 * READ-ONLY: this page mutates nothing -- every control is a filter.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchOwnerMoneyConsole } from '@/dashboard/api';
import type {
  ConsoleDrillVisitWire,
  ConsoleRollupRowWire,
  ConsolePeriod,
  OwnerMoneyConsoleRange,
  OwnerMoneyConsoleResponse,
} from '@/dashboard/types';
import { istTimeLabel } from '@/dashboard/money/money-desk-view';
import {
  CashStrip,
  DailyReportKpiBand,
  TreatmentRevenueSection,
  inrMinor,
  pctLabel,
  type DailyReportKpiKey,
  type ReportScope,
} from '@/dashboard/reporting';
import {
  AcBadge,
  AcButton,
  DataTable,
  DrawerShell,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  QuietAction,
  SectionContainer,
  SelectorBar,
  type DataTableColumn,
} from '@/dashboard/ui';

/** Re-exported so the SSR render pins keep ONE formatter import site; the
 *  definitions live in dashboard/reporting (shared with the other role
 *  lenses that will consume the same components). */
export { inrMinor, pctLabel };

/** Inline clinic rows before the rest moves behind the drawer (visual
 *  contract s.8a: a list caps inline, the full set opens in a drawer). */
export const CLINIC_INLINE_CAP = 5;

const IST_DAY_LABEL = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function dayLabel(businessDate: string): string {
  return IST_DAY_LABEL.format(new Date(`${businessDate}T06:00:00.000Z`));
}

export const PERIOD_RANGE_LABELS: Record<OwnerMoneyConsoleRange, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  this_month: 'This month',
  custom: 'Custom range',
};

/** The on-surface period label (TIME INTELLIGENCE, founder ruling
 *  2026-08-17): headers and titles follow the SELECTED period -- never
 *  "Today" while a range is selected. Exported for the SSR pins. */
export function periodLabel(period: ConsolePeriod): string {
  const from = dayLabel(period.fromDate);
  const to = dayLabel(period.toDate);
  const span = period.fromDate === period.toDate ? from : `${from} - ${to}`;
  if (period.range === 'custom') return span;
  return `${PERIOD_RANGE_LABELS[period.range]} \u00B7 ${span}`;
}

export type DrillKey =
  | 'walkins'
  | 'converted'
  | 'non_converted'
  | 'paid'
  | string;

/** Pure drill filter (LES-022: the drill consumes the SAME wire rows the
 *  headline derived from -- one list, filtered, never re-fetched). */
export function filterDrillVisits(
  day: Pick<import('@/dashboard/types').OwnerMoneyConsoleDayWire, 'drillVisits'>,
  drill: DrillKey,
): ConsoleDrillVisitWire[] {
  switch (drill) {
    case 'walkins':
      return [...day.drillVisits];
    case 'converted':
      return day.drillVisits.filter((v) => v.converted);
    case 'non_converted':
      return day.drillVisits.filter((v) => !v.converted);
    case 'paid':
      return day.drillVisits.filter((v) => v.collectedTodayMinorUnits > 0);
    default:
      return day.drillVisits.filter((v) => v.rowKeys.includes(drill));
  }
}

/** The doctor-closed outcome line -- PLAIN business words on the default
 *  glass (presentation corrective 2026-08-17 section B); the D-2 pair
 *  stays its OWN labeled count, never conflated with headline
 *  conversion. Exported so the SSR pin proves the on-surface wording. */
export function ConsoleHeadlineNotes({
  day,
}: {
  day: Pick<
    import('@/dashboard/types').OwnerMoneyConsoleDayWire,
    'disposition' | 'footnote'
  >;
}): JSX.Element {
  return (
    <p className="omc-caption" data-testid="omc-disposition">
      Doctor-closed outcomes: Converted {day.disposition.converted}
      {' \u00B7 '}Not converted {day.disposition.notConverted}
      {' \u00B7 '}
      {day.disposition.unclosed} not yet closed.
    </p>
  );
}

/** The definitions-and-sources affordance body (presentation corrective
 *  2026-08-17 section B: derivation detail and the ruled verbatim
 *  footnote live BEHIND a small information affordance, off the default
 *  glass). Exported so the SSR pin proves the verbatim footnote still
 *  renders and the definitions stay honest. */
export function ConsoleDefinitions({
  day,
}: {
  day: Pick<import('@/dashboard/types').OwnerMoneyConsoleDayWire, 'footnote'>;
}): JSX.Element {
  return (
    <div className="omc-definitions" data-testid="omc-definitions">
      <ul className="omc-deflist">
        <li>
          Walk-ins: the period&apos;s visits (cancelled excluded).
          Call-centre walk-ins are the bookings that came through the call
          centre; the rest count as other.
        </li>
        <li>
          Converted: the visit&apos;s patient made their first successful
          payment. The doctor&apos;s own closed outcome is shown separately
          and is never mixed into this number.
        </li>
        <li>
          Revenue: payments recorded in the selected period. Cash in the
          ledger below is the cash part of this same figure.
        </li>
        <li>
          Conversion rate and ticket size are recomputed from the selected
          period&apos;s own visits and revenue -- never an average of daily
          percentages.
        </li>
        <li>
          Incentives: the incentive rule is still to be ruled, so no
          incentive figure is calculated or shown.
        </li>
        <li>
          Treatments: the configured treatment rows. Treatments with
          activity are listed first; the rest open with the full set.
          Items map to treatments through an org setting; unmapped items
          are counted separately and never folded into another treatment,
          and revenue that cannot be attributed to a single treatment is
          disclosed rather than split by guesswork.
        </li>
        <li>
          Cash: read from the desk&apos;s own daily cash count, one clinic
          at a time. This report only reads -- it never counts, closes, or
          writes. A day whose count is not closed says so; it never shows
          a zero. Over a date range, opening is the first day&apos;s opening
          and closing is the last day&apos;s closing, while cash revenue,
          bank deposit and expenses are totals for the selected period.
        </li>
      </ul>
      <p className="omc-footnote" data-testid="omc-footnote">
        {day.footnote}
      </p>
    </div>
  );
}


/** Maps a KPI band cell to the drill list it opens. The band's vocabulary
 *  is the report's; the filter's vocabulary is the wire's. */
const KPI_DRILL: Record<DailyReportKpiKey, DrillKey> = {
  walkins: 'walkins',
  converted: 'converted',
  not_converted: 'non_converted',
  revenue: 'paid',
};

export default function OwnerMoneyConsolePage(): JSX.Element {
  const [range, setRange] = useState<OwnerMoneyConsoleRange>('today');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [doctorId, setDoctorId] = useState<number | null>(null);
  // Every drill defaults CLOSED: the owner first sees the answer; one
  // click reveals the visits behind a number.
  const [drill, setDrill] = useState<DrillKey | null>(null);
  const [clinicsDrawer, setClinicsDrawer] = useState(false);
  const [wire, setWire] = useState<OwnerMoneyConsoleResponse | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  // A refetch fault while the report is already rendered: keep the page
  // (fault-isolated posture) and say so inline -- never unmount the
  // filters mid-interaction.
  const [softError, setSoftError] = useState(false);

  const load = useCallback(async () => {
    // Custom waits for BOTH typed dates before it re-derives (an incomplete
    // range never fires a request, never fakes an answer).
    if (range === 'custom' && (customFrom === '' || customTo === '')) return;
    setState((prev) => (prev === 'ready' ? 'ready' : 'loading'));
    try {
      const data = await fetchOwnerMoneyConsole({
        range,
        ...(range === 'custom' ? { from: customFrom, to: customTo } : {}),
        clinicId,
        doctorId,
      });
      setWire(data);
      // The clinic's today = the server's default-range anchor, captured
      // once (never a client clock).
      setToday((prev) => prev ?? data.businessDate);
      setSoftError(false);
      setState('ready');
    } catch {
      // First load: the full honest error page. Refetch: keep the report
      // mounted (typing must survive) and disclose the fault inline.
      setState((prev) => (prev === 'ready' ? 'ready' : 'error'));
      setSoftError(true);
    }
  }, [range, customFrom, customTo, clinicId, doctorId]);

  useEffect(() => {
    // Custom typing debounces so segment-by-segment date states never fire
    // half-typed requests; the other ranges load immediately.
    const t = setTimeout(() => {
      void load();
    }, range === 'custom' ? 500 : 0);
    return () => clearTimeout(t);
  }, [load, range]);

  const day = wire?.day ?? null;

  const drillRows: ConsoleDrillVisitWire[] = useMemo(() => {
    if (!day || drill === null) return [];
    return filterDrillVisits(day, drill);
  }, [day, drill]);

  const drillTitle = useMemo(() => {
    if (!day || drill === null) return '';
    switch (drill) {
      case 'walkins':
        return 'Walk-ins (visits in period)';
      case 'converted':
        return 'Converted';
      case 'non_converted':
        return 'Not converted';
      case 'paid':
        return 'Paid in period';
      default: {
        const row = day.productRows.find((r) => r.key === drill);
        return row ? row.label : 'Visits';
      }
    }
  }, [day, drill]);

  const drillColumns: DataTableColumn<ConsoleDrillVisitWire>[] = useMemo(
    () => [
      {
        key: 'time',
        header: 'Time (IST)',
        render: (v: ConsoleDrillVisitWire) => istTimeLabel(v.appointmentTimeIso),
      },
      {
        key: 'patient',
        header: 'Patient',
        render: (v: ConsoleDrillVisitWire) =>
          `${v.patientName ?? 'Unnamed'}${v.umr ? ` \u00B7 ${v.umr}` : ''}`,
      },
      {
        key: 'doctor',
        header: 'Doctor',
        render: (v: ConsoleDrillVisitWire) => v.doctorName ?? '--',
      },
      {
        key: 'source',
        header: 'Source',
        render: (v: ConsoleDrillVisitWire) =>
          v.callCenterSourced ? 'Call-centre' : 'Other',
      },
      {
        key: 'collected',
        header: 'Collected',
        align: 'end',
        render: (v: ConsoleDrillVisitWire) =>
          inrMinor(v.collectedTodayMinorUnits),
      },
    ],
    [],
  );

  // FIVE COLUMNS AT 1440 (visual contract s.7). Cash is deliberately not a
  // column here: it is counted per clinic and shown in the cash strip when
  // one clinic is selected, never summed across branches.
  const clinicColumns: DataTableColumn<ConsoleRollupRowWire>[] = useMemo(
    () => [
      {
        key: 'clinic',
        header: 'Clinic',
        render: (r: ConsoleRollupRowWire) => (
          <QuietAction
            size="sm"
            className="omc-rowlink"
            onClick={() => {
              setClinicId(r.clinicId);
              setClinicsDrawer(false);
            }}
          >
            {r.clinicName}
          </QuietAction>
        ),
      },
      {
        key: 'walkins',
        header: 'Walk-ins',
        align: 'end',
        render: (r: ConsoleRollupRowWire) => String(r.walkins),
      },
      {
        key: 'converted',
        header: 'Converted',
        align: 'end',
        render: (r: ConsoleRollupRowWire) => String(r.converted),
      },
      {
        key: 'rate',
        header: 'Conversion',
        align: 'end',
        render: (r: ConsoleRollupRowWire) => pctLabel(r.ratePct),
      },
      {
        key: 'revenue',
        header: 'Revenue',
        align: 'end',
        render: (r: ConsoleRollupRowWire) => inrMinor(r.revenueMinorUnits),
      },
    ],
    [],
  );

  if (state === 'error') {
    return (
      <div className="ac-band-flow">
        <PageHeader
          title="Daily / EOD Report"
          subs={['All times IST']}
        />
        <ErrorState
          title="This report failed to load"
          detail="The report wire did not answer."
          action={
            <AcButton size="sm" onClick={() => void load()}>
              Retry
            </AcButton>
          }
        />
      </div>
    );
  }

  if (state === 'loading' || wire === null || day === null || today === null) {
    return (
      <div className="ac-band-flow">
        <PageHeader
          title="Daily / EOD Report"
          subs={['All times IST']}
        />
        <LoadingState label="Loading the daily report" />
      </div>
    );
  }

  const clinicLabel =
    wire.clinicId === null
      ? `All clinics (${wire.clinics.length})`
      : wire.clinics.find((c) => c.id === wire.clinicId)?.name ??
        `Clinic ${wire.clinicId}`;
  const doctorLabel =
    wire.doctorId === null
      ? null
      : wire.doctors.find((d) => d.id === wire.doctorId)?.name ?? null;
  // SCOPE IS COMPOSED HERE AND PASSED DOWN. The reporting components hold
  // no notion of "Owner" or "all clinics"; this page names the slice it is
  // authorised to read and hands it over as a parameter.
  const scope: ReportScope = {
    label: doctorLabel === null ? clinicLabel : `${clinicLabel} \u00B7 ${doctorLabel}`,
    periodLabel: periodLabel(wire.period),
    singleClinic: wire.clinicId !== null,
  };

  const clinicRows = wire.rollup ?? [];
  const anyCapped =
    wire.capped.appointments || wire.capped.items || wire.capped.payments;

  return (
    <div className="ac-band-flow" data-testid="owner-money-console">
      <PageHeader
        title="Daily / EOD Report"
        subs={['All times IST']}
        actions={
          <>
            <AcBadge tone="info">Owner - sees all clinics</AcBadge>
            <AcBadge>Read-only</AcBadge>
          </>
        }
      />

      {/* THE ONE PERIOD CONTROL (TIME INTELLIGENCE, founder ruling
          2026-08-17): Today default / Yesterday / Last 7 days / This month
          / Custom -- the SAME report re-derived for the selected period.
          Custom takes two typed IST business dates; period + clinic +
          doctor compose in ONE filter bar. */}
      <SelectorBar
        testId="omc-filters"
        controls={[
          {
            key: 'period',
            label: 'Period',
            value: range,
            onChange: (v) => setRange(v as OwnerMoneyConsoleRange),
            options: [
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'last7', label: 'Last 7 days' },
              { value: 'this_month', label: 'This month' },
              { value: 'custom', label: 'Custom range' },
            ],
          },
          {
            key: 'clinic',
            label: 'Clinic',
            value: wire.clinicId === null ? 'all' : String(wire.clinicId),
            onChange: (v) => setClinicId(v === 'all' ? null : Number(v)),
            options: [
              { value: 'all', label: `All clinics (${wire.clinics.length})` },
              ...wire.clinics.map((c) => ({
                value: String(c.id),
                label: c.name,
              })),
            ],
          },
          {
            key: 'doctor',
            label: 'Doctor',
            value: wire.doctorId === null ? 'all' : String(wire.doctorId),
            onChange: (v) => setDoctorId(v === 'all' ? null : Number(v)),
            options: [
              { value: 'all', label: 'All doctors' },
              ...wire.doctors.map((d) => ({
                value: String(d.id),
                label: d.name,
              })),
            ],
          },
        ]}
      >
        {range === 'custom' ? (
          <span className="omc-customrange" data-testid="omc-custom-range">
            {/* Uncontrolled inputs (the registered daybar's own input
                pattern): a controlled date input re-renders mid-typing and
                clobbers the year segment under real keystrokes -- the
                DOM owns the in-progress value; React reads each completed
                change. */}
            <label>
              <span className="omc-customrange__l">From</span>
              <input
                type="date"
                className="ac-daybar__input"
                defaultValue={customFrom}
                max={today ?? undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                data-testid="omc-custom-from"
              />
            </label>
            <label>
              <span className="omc-customrange__l">To</span>
              <input
                type="date"
                className="ac-daybar__input"
                defaultValue={customTo}
                max={today ?? undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                data-testid="omc-custom-to"
              />
            </label>
          </span>
        ) : null}
      </SelectorBar>

      {softError ? (
        <p className="omc-note" data-testid="omc-soft-error">
          The last refresh failed; showing the previous answer. Adjust a
          filter or retry.
        </p>
      ) : null}

      {anyCapped ? (
        <p className="omc-note" data-testid="omc-capped">
          Large day: some lists hit their read cap -- figures are lower
          bounds, not totals ({wire.capped.appointments ? 'visits ' : ''}
          {wire.capped.items ? 'items ' : ''}
          {wire.capped.payments ? 'payments' : ''}).
        </p>
      ) : null}

      {/* ---------- HERO: the six ruled business facts ---------- */}
      <DailyReportKpiBand
        scope={scope}
        facts={{
          walkins: day.walkins.total,
          callCenterSourced: day.walkins.callCenterSourced,
          otherWalkins: day.walkins.other,
          converted: day.conversion.converted,
          notConverted: day.conversion.nonConverted,
          conversionRatePct: day.conversion.ratePct,
          revenueMinorUnits: day.revenueMinorUnits,
          ticketSizeMinorUnits: day.ticketSizeMinorUnits,
        }}
        onDrill={(key) => setDrill(KPI_DRILL[key])}
        testId="omc-headline"
      >
        <ConsoleHeadlineNotes day={day} />
      </DailyReportKpiBand>

      {drill !== null ? (
        <SectionContainer
          title={`${drillTitle} \u00B7 ${periodLabel(wire.period)}`}
          count={drillRows.length}
          scope={scope.label}
          defaultOpen
          testId="omc-drill"
        >
          <p className="omc-caption">
            <QuietAction
              size="sm"
              onClick={() => setDrill(null)}
              data-testid="omc-drill-close"
            >
              Close
            </QuietAction>
          </p>
          <DataTable
            columns={drillColumns}
            rows={drillRows}
            rowKey={(v) => v.appointmentId}
            label={`${drillTitle} visits`}
            testId="omc-drill-table"
            empty={
              <EmptyState
                title="No visits behind this number"
                body="Nothing matched this number in the selected period."
              />
            }
          />
          <p className="omc-caption">
            Names and visit details only -- phone numbers and clinical
            records never appear here.
          </p>
        </SectionContainer>
      ) : null}

      {/* ---------- SUPPORTING: treatments, cash, incentives ---------- */}
      <TreatmentRevenueSection
        scope={scope}
        rows={day.productRows}
        columns={wire.treatmentColumns}
        unmappedItems={day.disclosures.unmappedItems}
        collectedMinorUnits={day.revenueMinorUnits}
        onDrillRow={(rowKey) => setDrill(rowKey)}
        testId="omc-product-rows"
      />

      <CashStrip scope={scope} ledger={wire.cashLedger} testId="omc-cash-ledger" />

      <p className="omc-incentive" data-testid="omc-incentive">
        Incentives: awaiting the incentive ruling -- no figure is
        calculated yet.
      </p>

      {/* ---------- DETAILS: clinics, then definitions ---------- */}
      {wire.mode === 'all' ? (
        <SectionContainer
          title="Clinics"
          count={clinicRows.length}
          scope={scope.periodLabel}
          testId="omc-rollup"
        >
          <DataTable
            columns={clinicColumns}
            rows={clinicRows.slice(0, CLINIC_INLINE_CAP)}
            rowKey={(r) => r.clinicId}
            label="Clinics"
            testId="omc-rollup-table"
            empty={
              <EmptyState
                title="No clinics in scope"
                body="The org has no clinics visible to this account."
              />
            }
          />
          {clinicRows.length > CLINIC_INLINE_CAP ? (
            <p className="omc-caption">
              <QuietAction
                size="sm"
                onClick={() => setClinicsDrawer(true)}
                data-testid="omc-rollup-showall"
              >
                {`Show all ${clinicRows.length}`}
              </QuietAction>
            </p>
          ) : null}
          {clinicsDrawer ? (
            <DrawerShell
              eyebrow={scope.periodLabel}
              title="All clinics"
              onClose={() => setClinicsDrawer(false)}
              testId="omc-rollup-drawer"
            >
              <DataTable
                columns={clinicColumns}
                rows={clinicRows}
                rowKey={(r) => r.clinicId}
                label="All clinics"
                testId="omc-rollup-drawer-table"
              />
            </DrawerShell>
          ) : null}
        </SectionContainer>
      ) : null}

      {/* The small information affordance: definitions, data sources, the
          ruled footnote, and the revenue reconciliation -- off the default
          glass, one click away, defaults CLOSED. */}
      <SectionContainer
        title="Definitions and data sources"
        count={8}
        stat={'plain-language definitions \u00B7 where each number comes from'}
        testId="omc-definitions-section"
      >
        <ConsoleDefinitions day={day} />
        <div className="omc-reconcile" data-testid="omc-reconcile">
          <p className="omc-caption">
            Revenue check for this period: treatments{' '}
            {inrMinor(
              day.productRows.reduce((s, r) => s + r.revenueMinorUnits, 0),
            )}{' '}
            + visits spanning several treatments{' '}
            {day.disclosures.multiRowVisits.visitCount} (
            {inrMinor(day.disclosures.multiRowVisits.revenueMinorUnits)}) +
            visits without a mapped item{' '}
            {day.disclosures.unattributedVisits.visitCount} (
            {inrMinor(day.disclosures.unattributedVisits.revenueMinorUnits)}) +
            payments not linked to a visit{' '}
            {inrMinor(day.disclosures.unlinkedPaymentsMinorUnits)} = revenue{' '}
            {inrMinor(day.revenueMinorUnits)}. A visit spanning several
            treatments is disclosed as one figure, never split by guesswork.
          </p>
        </div>
      </SectionContainer>
    </div>
  );
}
